import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CalendarDays, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameDay,
  parseISO,
  isToday,
} from "date-fns";
import { cn } from "@/lib/utils";

type CalendarTypeValue = "community" | "procurement" | "owner" | "pm";

interface CalEvent {
  id: string;
  title: string;
  start_time: string;
  all_day: boolean;
  calendar_type: CalendarTypeValue;
}

const CALENDAR_COLORS: Record<CalendarTypeValue, string> = {
  community: "bg-blue-500",
  pm: "bg-green-500",
  owner: "bg-purple-500",
  procurement: "bg-orange-500",
};

export function HomeWeekCalendar() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const { data } = await supabase
          .from("calendar_events")
          .select("id, title, start_time, all_day, calendar_type")
          .gte("start_time", weekStart.toISOString())
          .lte("start_time", weekEnd.toISOString())
          .order("start_time", { ascending: true });

        setEvents((data as CalEvent[]) || []);
      } catch (err) {
        console.error("Week calendar fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const getEventsForDay = (day: Date) =>
    events.filter((e) => isSameDay(parseISO(e.start_time), day));

  if (loading) {
    return <Skeleton className="h-36 rounded-lg mb-6" />;
  }

  return (
    <Card className="mb-6 border-border bg-card/80 backdrop-blur-sm">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          This Week
        </CardTitle>
        <button
          onClick={() => navigate("/calendar")}
          className="text-xs text-primary hover:underline flex items-center gap-0.5"
        >
          Full Calendar <ChevronRight className="h-3 w-3" />
        </button>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const dayEvents = getEventsForDay(day);
            const today = isToday(day);
            return (
              <button
                key={day.toISOString()}
                onClick={() => navigate("/calendar")}
                className={cn(
                  "flex flex-col items-center rounded-lg p-1.5 transition-colors hover:bg-muted/50 min-h-[72px]",
                  today && "bg-primary/5 ring-1 ring-primary/20"
                )}
              >
                <span className="text-[10px] font-medium text-muted-foreground uppercase">
                  {format(day, "EEE")}
                </span>
                <span
                  className={cn(
                    "text-sm font-bold mb-1",
                    today ? "text-primary" : "text-foreground"
                  )}
                >
                  {format(day, "d")}
                </span>
                <div className="flex flex-col gap-0.5 w-full">
                  {dayEvents.slice(0, 2).map((evt) => (
                    <div
                      key={evt.id}
                      className={cn(
                        "w-full h-1.5 rounded-full",
                        CALENDAR_COLORS[evt.calendar_type]
                      )}
                      title={evt.title}
                    />
                  ))}
                  {dayEvents.length > 2 && (
                    <span className="text-[9px] text-muted-foreground text-center">
                      +{dayEvents.length - 2}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
